import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type BoardCustomFieldValue } from '@air/api/types';

import {
  CustomFieldDateInput,
  type CustomFieldDateInputProps,
} from '~/components/CustomFields/CustomFieldInput/CustomFieldDateInput';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateItemsCustomFieldValue } from '~/utils/CustomFields/useUpdateItemsCustomFieldValue';

export type PrivateBoardCustomFieldDateInputProps = {
  field: BoardCustomFieldValue;
  trackingLocation: AirActionTrackingLocation;
  canEditCustomFields: boolean;
  boardId: string;
} & Omit<CustomFieldDateInputProps, 'onChange'>;

export const PrivateBoardCustomFieldDateInput = memo(
  ({ field, trackingLocation, canEditCustomFields, boardId, ...rest }: PrivateBoardCustomFieldDateInputProps) => {
    const { updateItemsCustomFieldValue } = useUpdateItemsCustomFieldValue();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceId = currentWorkspace?.id;

    const onChange: CustomFieldDateInputProps['onChange'] = useCallback(
      async (date) => {
        if (!workspaceId) {
          return;
        }
        await updateItemsCustomFieldValue({
          boardIds: [boardId],
          baseCustomField: field,
          nextDateValue: date?.toISOString(),
          trackingLocation,
          workspaceId,
        });
      },
      [boardId, field, trackingLocation, updateItemsCustomFieldValue, workspaceId],
    );

    return (
      <CustomFieldDateInput {...rest} field={field} onChange={onChange} canEditCustomFields={canEditCustomFields} />
    );
  },
);

PrivateBoardCustomFieldDateInput.displayName = 'PrivateBoardCustomFieldDateInput';
